/**
 * Excel de clientes para los préstamos parametrizados por clientes.
 *
 * Formato (primera hoja, la primera fila son los títulos):
 *
 *   | DNI      |            o bien    | ID_CLIENTE |
 *   |----------|                      |------------|
 *   | 30111222 |                      | 154321     |
 *   | 28999000 |                      | 98765      |
 *
 *  - Una columna llamada DNI (o DOCUMENTO) → se buscan por documento.
 *  - Una columna llamada ID_CLIENTE (o ID CLIENTE / IDCLIENTE) → se buscan por id de cliente.
 *  - Si hay las dos, se usa DNI. Si no hay títulos reconocibles se toma la primera columna como DNI.
 *  - Puntos, guiones y espacios se ignoran ("30.111.222" = 30111222). Filas vacías se saltean.
 *  - Puede haber otras columnas (nombre, observaciones…): se ignoran.
 */
import * as XLSX from "xlsx";

export const MAX_CLIENTES = 5000;

const normalizarTitulo = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase();

const TITULOS_DNI = ["DNI", "DOCUMENTO", "NRODOCUMENTO", "NRODOC", "DOC"];
const TITULOS_ID = ["IDCLIENTE", "CLIENTE", "IDSOCIO", "SOCIO"];

/** Separa un texto pegado ("30111222, 28999000\n...") en valores. */
export function separarValores(texto) {
  return String(texto ?? "")
    .split(/[\s,;]+/)
    .map((v) => v.replace(/[^\d]/g, ""))
    .filter(Boolean);
}

/**
 * Lee el Excel y devuelve { tipo: "DOCUMENTO"|"ID_CLIENTE", valores: string[], filasVacias }.
 * Lanza Error con un mensaje claro si el archivo no sirve.
 */
export async function leerClientesDeExcel(file) {
  const buffer = await file.arrayBuffer();
  const libro = XLSX.read(buffer, { type: "array" });
  const hoja = libro.Sheets[libro.SheetNames[0]];
  if (!hoja) throw new Error("El archivo no tiene hojas.");

  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, raw: false, defval: "" });
  if (filas.length === 0) throw new Error("El archivo está vacío.");

  const titulos = (filas[0] ?? []).map(normalizarTitulo);
  let col = titulos.findIndex((t) => TITULOS_DNI.includes(t));
  let tipo = "DOCUMENTO";
  if (col === -1) {
    col = titulos.findIndex((t) => TITULOS_ID.includes(t));
    if (col !== -1) tipo = "ID_CLIENTE";
  }

  let desde = 1;
  if (col === -1) {
    // Sin títulos reconocibles: primera columna como DNI; si la primera fila es un número, es dato
    col = 0;
    desde = /\d/.test(String(filas[0]?.[0] ?? "")) ? 0 : 1;
  }

  const valores = [];
  let filasVacias = 0;
  for (const fila of filas.slice(desde)) {
    const v = String(fila?.[col] ?? "").replace(/[^\d]/g, "");
    if (v) valores.push(v);
    else filasVacias += 1;
  }

  if (valores.length === 0) {
    throw new Error("No se encontraron clientes en el archivo. Revisá que la columna se llame DNI o ID_CLIENTE.");
  }
  if (valores.length > MAX_CLIENTES) {
    throw new Error(`El archivo tiene ${valores.length} filas; el máximo por préstamo es ${MAX_CLIENTES}.`);
  }
  return { tipo, valores, filasVacias };
}

/** Descarga una plantilla de ejemplo con instrucciones. */
export function descargarPlantillaClientes() {
  const libro = XLSX.utils.book_new();

  const datos = XLSX.utils.aoa_to_sheet([
    ["DNI", "NOMBRE (opcional, no se usa)"],
    ["30111222", "PEREZ, ANA"],
    ["28999000", "GOMEZ, LUIS"],
  ]);
  datos["!cols"] = [{ wch: 14 }, { wch: 32 }];
  XLSX.utils.book_append_sheet(libro, datos, "Clientes");

  const ayuda = XLSX.utils.aoa_to_sheet([
    ["Cómo completar el archivo"],
    ["1. Usá la hoja 'Clientes'. La primera fila son los títulos: no la borres."],
    ["2. Poné un cliente por fila en la columna DNI (sin puntos, aunque con puntos también se acepta)."],
    ["3. Si preferís cargar por ID de cliente, cambiá el título DNI por ID_CLIENTE y poné los ids."],
    ["4. Las demás columnas son opcionales y se ignoran."],
    [`5. Máximo ${MAX_CLIENTES} clientes por préstamo. Los repetidos se cargan una sola vez.`],
    ["6. Al subirlo, la intranet valida cada cliente y te muestra los que no se encontraron."],
  ]);
  ayuda["!cols"] = [{ wch: 95 }];
  XLSX.utils.book_append_sheet(libro, ayuda, "Instrucciones");

  XLSX.writeFile(libro, "plantilla-clientes-prestamo.xlsx");
}
